<template>
  <BbPopup v-model="isOpened" :showCloseIcon="true" contentClass="create-collection-dialog glossary-csv-upload-dialog">
    <template #popup-header>
      <TextBody tag="span" i18n-key="glossaries.uploadPopup.title" :body-type="3" weight="medium" />
    </template>
    <div
      class="drop-zone"
      :class="{ 'is-dragging': isDragging, 'has-error': !!errorMessage, 'is-uploaded': !!file }"
      :role="file ? undefined : 'button'"
      :tabindex="file ? undefined : 0"
      @click="!file && openFilePicker()"
      @keydown.enter.prevent="!file && openFilePicker()"
      @keydown.space.prevent="!file && openFilePicker()"
      @dragenter.prevent="isDragging = true"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <input ref="fileInput" type="file" accept=".csv,text/csv" class="file-input" @change="onFileSelected" />
      <div class="drop-zone-icon">
        <BbIcon :name="file ? 'checkmark-small' : 'download'" size="small" />
      </div>
      <template v-if="file">
        <span class="drop-zone-title">{{ t('glossaries.uploadPopup.uploadedTitle') }}</span>
        <TextBody
          class="file-name"
          tag="span"
          i18n-key="glossaries.uploadPopup.selectedFile"
          :i18n-params="{ name: file.name }"
          :body-type="2"
          color="#000000"
        />
        <BbBtn class="replace-button" size="small" type="secondary" @click.stop="openFilePicker">
          <TextBody i18n-key="glossaries.uploadPopup.replaceFile" />
        </BbBtn>
      </template>
      <template v-else>
        <span class="drop-zone-title">{{ t('glossaries.uploadPopup.dropTitle') }}</span>
        <TextBody tag="span" i18n-key="glossaries.uploadPopup.dropHint" :body-type="2" color="#000000" />
      </template>
    </div>
    <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
    <template #popup-actions>
      <BbBtn type="secondary" @click="isOpened = false">
        <TextBody i18n-key="common.cancel" />
      </BbBtn>
      <BbBtn color="#6222e3" @click="save" :is-disabled="!file">
        <TextBody i18n-key="common.save" />
      </BbBtn>
    </template>
  </BbPopup>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TextBody from '~/components/common/TextBody';

export default {
  components: { TextBody },
  props: {
    popupOpened: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:open', 'execute:action'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const fileInput = ref(null);
    const file = ref(null);
    const isDragging = ref(false);
    const errorMessage = ref('');

    const isOpened = computed({
      get: () => props.popupOpened,
      set: (val) => emit('update:open', val),
    });

    const openFilePicker = () => fileInput.value?.click();

    const selectFile = (selected) => {
      if (!selected) return;
      if (!selected.name.toLowerCase().endsWith('.csv')) {
        file.value = null;
        errorMessage.value = t('glossaries.csvInvalidFile');
        return;
      }
      errorMessage.value = '';
      file.value = selected;
    };

    const onFileSelected = (event) => {
      selectFile(event.target.files[0]);
      // reset so selecting the same file again still fires change
      event.target.value = '';
    };

    const onDrop = (event) => {
      isDragging.value = false;
      selectFile(event.dataTransfer?.files?.[0]);
    };

    const save = () => {
      if (file.value) emit('execute:action', file.value);
    };

    // start empty every time the popup is reopened
    watch(isOpened, (opened) => {
      if (opened) {
        file.value = null;
        errorMessage.value = '';
        isDragging.value = false;
      }
    });

    return {
      errorMessage,
      file,
      fileInput,
      isDragging,
      isOpened,
      onDrop,
      onFileSelected,
      t,
      openFilePicker,
      save,
    };
  },
};
</script>

<style scoped lang="scss">
.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $bb-spacing-1;
  padding: $bb-spacing-6;
  border: 1px dashed transparent;
  border-radius: 12px;
  background-color: $bb-neutral-5;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: $bb-neutral-3;
    outline: none;
  }

  &.is-dragging {
    border-color: $bb-primary-3;
    background-color: $bb-neutral-0;
  }

  &.has-error {
    border-color: $bb-interface-5;
  }

  .file-input {
    display: none;
  }

  &.is-uploaded {
    cursor: default;

    &:hover {
      border-color: transparent;
    }

    .drop-zone-icon {
      border: 2px solid $bb-interface-1;
      background-color: $bb-neutral-0;

      :deep(.bb-icon) {
        color: $bb-interface-1;
      }
    }

    .replace-button {
      margin-top: $bb-spacing-2;
    }
  }

  .drop-zone-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin-bottom: $bb-spacing-2;
    border-radius: 50%;
    background-color: $bb-interface-8;

    // BbIcon is an icon font, so the colour comes from CSS (the library sets it to near-black)
    :deep(.bb-icon) {
      background-color: transparent;
      color: $bb-primary-3;
    }
  }

  .drop-zone-title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;
    color: $bb-neutral-1;
  }

  :deep(.file-name) {
    word-break: break-all;
  }
}

.error-message {
  display: block;
  @include bb-regular-text-styles('sm');
  margin-top: $bb-spacing-2;
  color: $bb-interface-5;
}
</style>
